/**
 * Default export formatters — transform canonical blocks to pre-formatted
 * markdown, excel, and docx shapes.
 *
 * Vendors may override via Parser.export(); this is the fallback.
 */

import { Data, Effect, Schema, SchemaIssue } from 'effect';
import { CanonicalBlockSchema, type BlockType, type CanonicalBlock } from './types';

export type ParserFormattingIssue = {
  readonly path: ReadonlyArray<PropertyKey>;
};

/** Redacted formatting failure that never retains parser text, labels, or vendor metadata. */
export class ParserFormattingError extends Data.TaggedError('ParserFormattingError')<{
  readonly operation: 'default-export';
  readonly issues: ReadonlyArray<ParserFormattingIssue>;
}> {}

const formatStandardSchemaIssue = SchemaIssue.makeFormatterStandardSchemaV1();

function parserFormattingError(cause: unknown): ParserFormattingError {
  const issues: ReadonlyArray<ParserFormattingIssue> = Schema.isSchemaError(cause)
    ? formatStandardSchemaIssue(cause.issue).issues.map((issue) => ({
        path: issue.path?.map((segment) =>
          typeof segment === 'object' ? segment.key : segment,
        ) ?? [],
      }))
    : [{ path: [] }];
  return new ParserFormattingError({ operation: 'default-export', issues });
}

/**
 * Default fallback: resolve a raw vendor type to a canonical BlockType.
 * Used when no vendor plugin is available or plugin.resolveType() returns undefined.
 */
export function resolveBlockType(raw: string, plugin?: { resolveType?(rawType: string): BlockType | undefined }): BlockType {
  const fromPlugin = plugin?.resolveType?.(raw);
  if (fromPlugin) return fromPlugin;

  const lower = raw.toLowerCase();
  if (lower === 'table') return 'table';
  if (lower === 'row') return 'row';
  if (lower === 'cell') return 'cell';
  if (lower === 'header' || lower === 'title' || lower === 'heading') return 'header';
  if (lower === 'footer') return 'footer';
  if (lower === 'figure' || lower === 'image') return 'figure';
  if (lower === 'key_value') return 'key_value';
  if (lower === 'list' || lower === 'listitem') return 'list';
  return 'text';
}

// ── Pre-formatted output shapes ─────────────────────────────────────

export interface PreFormattedMarkdown {
  pages: Array<{ pageNumber: number; content: string; vendor: string }>;
  pageCount: number;
}

export interface TablePreview {
  pageNumber: number;
  tableIndex: number;
  headers: string[];
  rows: string[][];
}

export interface PreFormattedExcel {
  sheets: Array<{ name: string; pageNumber: number; tables: TablePreview[] }>;
  totalTables: number;
}

export interface PreFormattedDocx {
  pages: Array<{ pageNumber: number; content: string }>;
  totalPages: number;
}

// ── Vendor export result (what Parser.export() returns) ───────

export interface ParserExportResult {
  markdown: Array<{ pageNumber: number; content: string }>;
  tables: Array<TablePreview>;
}

// ── Block → Markdown ────────────────────────────────────────────────

export function blockToMarkdown(block: CanonicalBlock): string {
  switch (resolveBlockType(block.type)) {
    case 'header':
      return `## ${block.value || ''}`;
    case 'footer':
      return `---\n${block.value || ''}`;
    case 'text':
    case 'key_value':
    case 'list':
      return block.value || '';
    case 'figure':
      return block.label ? `[Figure: ${block.label}]` : '[Figure]';
    case 'table':
      return tableBlockToMarkdown(block);
    default:
      return block.value || '';
  }
}

function tableBlockToMarkdown(table: CanonicalBlock): string {
  const rows = (table.children || []).filter(c => c.type === 'row');
  if (rows.length === 0) return table.value || '';

  const lines: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const cells = (rows[i].children || []).filter(c => c.type === 'cell');
    const cellValues = cells.map(c => (c.value || '').replace(/\|/g, '\\|'));
    lines.push(`| ${cellValues.join(' | ')} |`);

    if (i === 0) {
      lines.push(`| ${cellValues.map(() => '---').join(' | ')} |`);
    }
  }
  return lines.join('\n');
}

// ── Extract structured tables from blocks ───────────────────────────

export function extractTablesFromBlocks(
  blocks: CanonicalBlock[],
  pageNumber: number,
): TablePreview[] {
  const tables: TablePreview[] = [];
  let tableIndex = 0;

  for (const block of blocks) {
    if (block.type !== 'table') continue;

    const rows = (block.children || []).filter(c => c.type === 'row');
    if (rows.length === 0) continue;

    const allRows = rows.map(row =>
      (row.children || []).filter(c => c.type === 'cell').map(c => c.value || '')
    );

    const headers = allRows[0] || [];
    const bodyRows = allRows.slice(1);

    tables.push({
      pageNumber,
      tableIndex: tableIndex++,
      headers,
      rows: bodyRows,
    });
  }

  return tables;
}

// ── Default export (used when vendor has no custom export()) ─────────

export interface SnapshotPage {
  pageNumber: number;
  blocks: CanonicalBlock[];
  detection: { vendor: string; pass: number; confidence: number | null };
}

const SnapshotPageInputSchema = Schema.Struct({
  pageNumber: Schema.Finite,
  blocks: Schema.mutable(Schema.Array(CanonicalBlockSchema)),
  detection: Schema.Struct({
    vendor: Schema.String,
    pass: Schema.Finite,
    confidence: Schema.NullOr(Schema.Finite),
  }),
});

const SnapshotPagesInputSchema = Schema.mutable(Schema.Array(SnapshotPageInputSchema));

function defaultExportUnsafe(pages: SnapshotPage[]): ParserExportResult {
  const markdown: ParserExportResult['markdown'] = [];
  const tables: TablePreview[] = [];

  for (const page of pages) {
    const parts = page.blocks.map(blockToMarkdown).filter(Boolean);
    markdown.push({
      pageNumber: page.pageNumber,
      content: parts.join('\n\n'),
    });

    const pageTables = extractTablesFromBlocks(page.blocks, page.pageNumber);
    tables.push(...pageTables);
  }

  return { markdown, tables };
}

/** Native fallback-export boundary for decoded parser pages. */
export const defaultExportEffect = Effect.fn('ParserSdkFormatters.defaultExport')(
  function* (input: unknown) {
    const pages = yield* Schema.decodeUnknownEffect(SnapshotPagesInputSchema, {
      errors: 'all',
    })(input).pipe(Effect.mapError(parserFormattingError));
    return defaultExportUnsafe(pages as unknown as SnapshotPage[]);
  },
);

/** Existing synchronous formatter derived from the validated native program. */
export function defaultExport(pages: SnapshotPage[]): ParserExportResult {
  return Effect.runSync(defaultExportEffect(pages));
}

// ── Build pre-formatted shapes from ParserExportResult ──────────────

export function buildPreFormattedMarkdown(
  pages: SnapshotPage[],
  exportResult: ParserExportResult,
): PreFormattedMarkdown {
  return {
    pages: pages.map((p, i) => ({
      pageNumber: p.pageNumber,
      content: exportResult.markdown[i]?.content ?? '',
      vendor: p.detection.vendor,
    })),
    pageCount: pages.length,
  };
}

export function buildPreFormattedExcel(
  exportResult: ParserExportResult,
): PreFormattedExcel {
  const sheets: PreFormattedExcel['sheets'] = [];
  let totalTables = 0;

  // Group tables by page
  const byPage = new Map<number, TablePreview[]>();
  for (const table of exportResult.tables) {
    if (!byPage.has(table.pageNumber)) byPage.set(table.pageNumber, []);
    byPage.get(table.pageNumber)!.push(table);
  }

  for (const [pageNum, pageTables] of [...byPage.entries()].sort(([a], [b]) => a - b)) {
    totalTables += pageTables.length;
    if (pageTables.length === 1) {
      sheets.push({ name: `${pageNum}`, pageNumber: pageNum, tables: [pageTables[0]] });
    } else {
      pageTables.forEach((table, idx) => {
        const letter = String.fromCharCode(65 + idx);
        sheets.push({ name: `${pageNum}-${letter}`, pageNumber: pageNum, tables: [table] });
      });
    }
  }

  return { sheets, totalTables };
}

export function buildPreFormattedDocx(
  exportResult: ParserExportResult,
): PreFormattedDocx {
  return {
    pages: exportResult.markdown.map(m => ({
      pageNumber: m.pageNumber,
      content: m.content,
    })),
    totalPages: exportResult.markdown.length,
  };
}
